import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IndicatorService } from '../../services/indicatorService';
import { Indicator, getIndicatorHealth } from '../../types/indicator';
import { TrendingUp, Plus, Gauge, ChevronRight } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, YAxis } from 'recharts';
import { useAppToast } from '../AppToast';
import { useProjectStore } from '../../store/useProjectStore';

interface ContextualKPIProps {
  projectId?: string;
  issueId?: string;
  organizationId: string;
  layout?: 'grid' | 'sidebar';
  onRequestBindKpi?: () => void;
}

const HEALTH_COLOR: Record<string, string> = {
  healthy: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  unknown: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

const HEALTH_BORDER: Record<string, string> = {
  healthy: 'border-l-emerald-500',
  warning: 'border-l-amber-500',
  critical: 'border-l-red-500',
  unknown: 'border-l-slate-300 dark:border-l-slate-600',
};

const EMPTY_KPI_BINDINGS: Array<{ lastReadingDate: string; name: string; unit: string; type?: Indicator['type']; currentValue: number; targetValue?: number | null }> = [];

/**
 * Sparkline com dados REAIS de indicator_snapshots.
 * - Para KPIs locais (id 'local-kpi-…') não há histórico em DB → mostra ponto único.
 * - Para indicadores persistidos: busca até 20 snapshots e plota cronologicamente.
 */
const KpiSparkline: React.FC<{
  organizationId: string;
  indicatorId: string;
  currentValue: number;
  color: string;
  gradientId: string;
}> = ({ organizationId, indicatorId, currentValue, color, gradientId }) => {
  const isLocal = indicatorId.startsWith('local-kpi-');
  const { data: snapshots = [] } = useQuery({
    queryKey: ['indicator-snapshots', organizationId, indicatorId],
    queryFn: () => IndicatorService.getSnapshots(organizationId, indicatorId, 20),
    enabled: !isLocal && Boolean(indicatorId),
    staleTime: 30_000,
  });

  // Ordem cronológica ascendente (getSnapshots retorna desc)
  const chartData = useMemo(() => {
    if (snapshots.length === 0) {
      // Sem histórico: linha plana com o valor atual (2 pontos para renderizar a área)
      return [
        { t: 0, valor: currentValue },
        { t: 1, valor: currentValue },
      ];
    }
    const asc = [...snapshots].reverse();
    return asc.map((s, idx) => ({ t: idx, valor: Number(s.value) }));
  }, [snapshots, currentValue]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis hide domain={[(dataMin: number) => dataMin - 1, (dataMax: number) => dataMax + 1]} />
        <Area
          type="monotone"
          dataKey="valor"
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const ContextualKPI: React.FC<ContextualKPIProps> = ({ projectId, issueId, organizationId, layout = 'grid', onRequestBindKpi }) => {
  const queryClient = useQueryClient();
  const { showToast } = useAppToast();
  const [selectedInd, setSelectedInd] = useState<Indicator | null>(null);
  const [newValue, setNewValue] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const localProjectKpisRaw = useProjectStore((state) => {
    if (!projectId) return undefined;
    const project = state.projects.find((item) => item.id === projectId);
    return project?.estado_atual?.kpis;
  });
  const localProjectKpis = localProjectKpisRaw ?? EMPTY_KPI_BINDINGS;

  const { data: indicators = [], isLoading } = useQuery({
    queryKey: ['indicators', organizationId, { project_id: projectId, issue_id: issueId }],
    queryFn: () => IndicatorService.list(organizationId, { project_id: projectId, issue_id: issueId }),
  });

  const unifiedIndicators = useMemo(() => {
    if (!projectId) return indicators;

    const fromLocal = localProjectKpis.map((kpi, index) => ({
      id: `local-kpi-${kpi.id || index}`,
      organization_id: organizationId,
      name: kpi.name,
      type: (kpi.type ?? 'custom') as Indicator['type'],
      unit: kpi.unit,
      target_value: kpi.targetValue ?? null,
      current_value: kpi.currentValue,
      previous_value: null,
      threshold_warning: null,
      threshold_critical: null,
      direction: 'target_is_best' as const,
      status: 'active' as const,
      frequency: 'daily' as const,
      project_id: projectId,
      issue_id: null,
      visibility_scope: 'project' as const,
      owner_id: null,
      created_by: null,
      tags: ['bound_kpi'],
      created_at: kpi.lastReadingDate,
      updated_at: kpi.lastReadingDate,
    } satisfies Indicator));

    const byName = new Map<string, Indicator>();
    for (const indicator of [...indicators, ...fromLocal]) {
      byName.set(indicator.name.trim().toLowerCase(), indicator);
    }
    return Array.from(byName.values());
  }, [indicators, localProjectKpis, organizationId, projectId]);

  const handleSaveValue = async () => {
    if (!selectedInd) return;
    // KPIs locais (ainda não persistidos) não suportam edição
    if (selectedInd.id.startsWith('local-kpi-')) {
      showToast('Este KPI ainda não foi persistido. Reabra a Jornada do Projeto para sincronizar.', 'error');
      return;
    }

    const parsedValue = newValue.trim() === '' ? null : Number(newValue);
    const parsedTarget = newTarget.trim() === '' ? null : Number(newTarget);
    const valueChanged = parsedValue != null && Number.isFinite(parsedValue) && parsedValue !== selectedInd.current_value;
    const targetChanged = parsedTarget !== (selectedInd.target_value ?? null) && (parsedTarget === null || Number.isFinite(parsedTarget));

    if (!valueChanged && !targetChanged) {
      setSelectedInd(null);
      setNewValue('');
      setNewTarget('');
      return;
    }

    try {
      setSaving(true);
      if (valueChanged) {
        await IndicatorService.recordSnapshot(organizationId, selectedInd.id, parsedValue as number);
      }
      if (targetChanged) {
        await IndicatorService.update(organizationId, selectedInd.id, { target_value: parsedTarget });
      }
      showToast('Indicador atualizado com sucesso!', 'success');
      queryClient.invalidateQueries({ queryKey: ['indicators'] });
      queryClient.invalidateQueries({ queryKey: ['indicator-snapshots', organizationId, selectedInd.id] });
      setSelectedInd(null);
      setNewValue('');
      setNewTarget('');
    } catch (e) {
      showToast('Falha ao atualizar indicador', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-sm text-[var(--text-muted)] animate-pulse">Carregando indicadores...</div>;
  }

  if (unifiedIndicators.length === 0) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center p-12 text-center bg-[var(--bg-elevated)] border border-[var(--border-default)] border-dashed rounded m-1">
        <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded flex items-center justify-center mb-4 shadow-sm ring-1 ring-blue-100 dark:ring-blue-800">
          <Gauge size={28} strokeWidth={1.5} />
        </div>
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
          Nenhum KPI Vinculado
        </h3>
        <p className="text-[var(--text-muted)] mb-6 max-w-md text-[13px] leading-relaxed">
          {projectId 
            ? 'Este projeto ainda não possui indicadores de sustentação associados. Vincule métricas para monitorar o ROI e a performance da operação.'
            : 'Este problema/MASP ainda não possui indicadores para validação de eficácia vinculados.'}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => onRequestBindKpi?.()}
            className="px-5 py-2.5 bg-[#0066FF] hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors shadow-sm hover:shadow-md flex items-center gap-2 group"
          >
            <Plus size={16} className="group-hover:scale-110 transition-transform" />
            Vincular KPI
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid gap-4 ${
      layout === 'sidebar' 
        ? 'grid-cols-1' 
        : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
    }`}>
      {unifiedIndicators.map(indicator => {
        const health = getIndicatorHealth(indicator);
        const currentValue = Number(indicator.current_value) || 0;
        const sparklineColor = health === 'critical' ? '#EF4444' : health === 'warning' ? '#F59E0B' : '#10B981';
        const gradientId = `colorKpi-${indicator.id}`;

        return (
          <div 
            key={indicator.id}
            className={`bg-[var(--bg-surface)] p-4 rounded border border-[var(--border-default)] shadow-sm hover:shadow relative overflow-hidden group transition-all hover:border-[var(--border-hover)] border-l-4 ${HEALTH_BORDER[health]}`}
          >
            {selectedInd?.id === indicator.id ? (
               <div className="absolute inset-0 bg-white/95 dark:bg-slate-900/95 z-20 backdrop-blur-[2px] p-4 flex flex-col justify-center animate-in fade-in zoom-in duration-200">
                  <h4 className="text-[12px] font-bold mb-3">Atualizar Indicador ({indicator.unit})</h4>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Valor Atual</label>
                      <input 
                        type="number"
                        autoFocus
                        value={newValue}
                        onChange={e => setNewValue(e.target.value)}
                        placeholder={String(indicator.current_value ?? '')}
                        className="w-full rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm h-8"
                        onKeyDown={e => e.key === 'Enter' && handleSaveValue()}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Meta (Target)</label>
                      <input 
                        type="number"
                        value={newTarget}
                        onChange={e => setNewTarget(e.target.value)}
                        placeholder={indicator.target_value != null ? String(indicator.target_value) : 'opcional'}
                        className="w-full rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm h-8"
                        onKeyDown={e => e.key === 'Enter' && handleSaveValue()}
                      />
                    </div>
                  </div>
                  <button 
                    onClick={handleSaveValue}
                    disabled={saving}
                    className="w-full h-8 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50 text-xs shadow-sm flex items-center justify-center"
                  >
                     {saving ? 'Salvando…' : 'Salvar'}
                  </button>
                  <button 
                    onClick={() => { setSelectedInd(null); setNewValue(''); setNewTarget(''); }}
                    className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1"
                  >
                    ×
                  </button>
               </div>
            ) : null}

            {/* Header: Nome + Badge */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--text-muted)] block mb-1">
                  {indicator.type.replace('_', ' ')}
                </span>
                <h3 className="text-[13px] font-bold text-[var(--text-primary)] leading-snug line-clamp-2" title={indicator.name}>
                  {indicator.name}
                </h3>
              </div>
              <div className={`px-2 py-0.5 rounded-sm text-[10px] font-medium whitespace-nowrap shadow-sm border border-black/5 ${HEALTH_COLOR[health]}`}>
                 {indicator.current_value} {indicator.unit}
              </div>
            </div>

            {/* Sparkline (Visual Management) — histórico real de indicator_snapshots */}
            <div className="h-10 w-full mb-4 opacity-60 group-hover:opacity-100 transition-opacity">
              <KpiSparkline
                organizationId={organizationId}
                indicatorId={indicator.id}
                currentValue={currentValue}
                color={sparklineColor}
                gradientId={gradientId}
              />
            </div>

            {/* Meta */}
            <div className="flex items-center justify-between text-[11px] text-[var(--text-muted)] pt-3 border-t border-[var(--border-default)]">
               <div>
                 Target: <span className="font-medium text-[var(--text-primary)]">{indicator.target_value ?? 'N/A'} {indicator.unit}</span>
               </div>
               <button 
                  onClick={() => {
                    setSelectedInd(indicator);
                    setNewValue('');
                    setNewTarget(indicator.target_value != null ? String(indicator.target_value) : '');
                  }}
                  className="flex items-center gap-1 group-hover:text-blue-600 transition-colors font-semibold"
               >
                  Atualizar <TrendingUp size={12} />
               </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
